import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { refreshCart } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setSelectedImg(0);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      toast.error("Product not found");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  // SEO metadata (imperative — reliable under React 19, avoids Helmet conflicts)
  useEffect(() => {
    if (!product) return;
    const title = product.seo_title || `${product.name} - Buy Online | OrgShop`;
    const desc = product.seo_description || `${(product.description || "").substring(0, 155)}...`;
    document.title = title;

    const setMeta = (key, attr, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value || "");
    };
    setMeta("description", "name", desc);
    setMeta("og:title", "property", product.name);
    setMeta("og:description", "property", product.description || "");
    setMeta("og:image", "property", product.images?.[0] || "");
    setMeta("og:type", "property", "product");
    setMeta("product:price:amount", "property", String(product.offer_price || product.price || ""));
    setMeta("product:price:currency", "property", "INR");
  }, [product]);

  const handleAddToCart = async () => {
    if (!token) {
      toast.error("Please login to add items to cart");
      return;
    }

    setAddingToCart(true);
    try {
      await axios.post(
        `${API}/cart/add`,
        { product_id: product.id, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshCart();
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="product-detail-loading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const fallbackImg = "https://images.unsplash.com/photo-1564682628523-316b685c5d7b?w=800";
  const images = product.images && product.images.length > 0 ? product.images : [fallbackImg];

  return (
    <div data-testid="product-detail-page">
      <Navbar />

      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-8 hover:bg-primary/10"
            data-testid="product-back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="space-y-4">
              <div className="aspect-square rounded-2xl overflow-hidden bg-muted shadow-soft">
                <img
                  src={images[selectedImg] || fallbackImg}
                  alt={product.name}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  data-testid="product-detail-image"
                />
              </div>
              {images.length > 1 && (
                <div className="grid grid-cols-5 gap-3" data-testid="product-thumbnails">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedImg(idx)}
                      className={`aspect-square rounded-xl overflow-hidden bg-muted border-2 transition-colors ${
                        selectedImg === idx ? "border-primary" : "border-transparent hover:border-primary/40"
                      }`}
                      data-testid={`product-thumbnail-${idx}`}
                    >
                      <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <h1 className="font-heading text-4xl font-bold text-primary mb-4" data-testid="product-detail-name">
                {product.name}
              </h1>
              <div className="flex items-baseline space-x-4 mb-6">
                {product.discount_percentage > 0 ? (
                  <>
                    <span className="text-2xl text-muted-foreground line-through">₹{product.actual_price}</span>
                    <span className="font-heading text-5xl font-bold text-primary" data-testid="product-detail-price">
                      ₹{product.offer_price}
                    </span>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                      {product.discount_percentage}% OFF
                    </span>
                  </>
                ) : (
                  <span className="font-heading text-5xl font-bold text-primary" data-testid="product-detail-price">
                    ₹{product.offer_price || product.price}
                  </span>
                )}
              </div>

              <div className="border-t border-b border-border py-6 mb-6">
                <p className="text-muted-foreground leading-relaxed" data-testid="product-detail-description">
                  {product.description}
                </p>
              </div>

              <div className="mb-6">
                <p className="text-sm font-medium text-foreground mb-2">Stock: {product.stock > 0 ? `${product.stock} available` : "Out of stock"}</p>
              </div>

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-foreground mb-3">Quantity</label>
                <div className="flex items-center space-x-4">
                  <Button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    variant="outline"
                    size="icon"
                    className="rounded-full w-12 h-12"
                    data-testid="product-quantity-decrease-btn"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-2xl font-bold w-16 text-center" data-testid="product-quantity-value">{quantity}</span>
                  <Button
                    onClick={() => setQuantity(quantity + 1)}
                    variant="outline"
                    size="icon"
                    className="rounded-full w-12 h-12"
                    data-testid="product-quantity-increase-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart || product.stock === 0}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full py-6 text-lg font-medium"
                data-testid="product-add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {addingToCart ? "Adding..." : product.stock === 0 ? "Out of Stock" : "Add to Cart"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
